import React from 'react';
import { sanitizeHtml } from '../utils/sanitizeHtml';

interface Arcanum {
  number: number;
  name: string;
  keywords: string;
  positive: string;
  negative: string;
}

interface DestinyMatrixData {
  center: Arcanum;
  corners: Arcanum[];
  edges: Arcanum[];
  keyPoints: Arcanum[];
  svg: string;
  aiInterpretation?: string;
}

interface Props {
  data: DestinyMatrixData;
}

const DestinyMatrix: React.FC<Props> = ({ data }) => {
  const { center, corners, edges } = data;
  const cornerLabels = ['Духовная', 'Материальная', 'Кармическая', 'Баланс'];

  return (
    <div style={{
      background: 'rgba(10, 20, 40, 0.85)',
      border: '2px solid rgba(147, 130, 220, 0.5)',
      borderRadius: '20px',
      padding: '30px',
      backdropFilter: 'blur(10px)',
      boxShadow: '0 8px 32px rgba(147, 130, 220, 0.2)',
    }}>
      <h2 style={{
        color: '#c4b5fd',
        fontSize: '24px',
        fontWeight: 'bold',
        textAlign: 'center',
        marginBottom: '24px',
        fontFamily: 'serif',
      }}>
        Матрица Судьбы
      </h2>

      {/* SVG visualization */}
      <div style={{
        maxWidth: '400px',
        margin: '0 auto 24px',
        borderRadius: '12px',
        overflow: 'hidden',
        background: 'rgba(10, 5, 30, 0.5)',
      }}
        dangerouslySetInnerHTML={{ __html: sanitizeHtml(data.svg) }}
      />

      {/* Key Points */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: '12px',
        marginBottom: '20px',
      }}>
        {/* Center */}
        <div style={{
          gridColumn: '1 / -1',
          background: 'rgba(60, 40, 120, 0.6)',
          border: '2px solid rgba(200, 180, 255, 0.4)',
          borderRadius: '12px',
          padding: '14px',
          textAlign: 'center',
        }}>
          <div style={{
            fontSize: '11px',
            color: 'rgba(200, 180, 255, 0.6)',
            textTransform: 'uppercase',
            letterSpacing: '1px',
            marginBottom: '4px',
          }}>
            Центр — Ядро личности
          </div>
          <div style={{
            fontSize: '28px',
            fontWeight: 'bold',
            color: '#e0d0ff',
            marginBottom: '4px',
          }}>
            {center.number}
          </div>
          <div style={{
            fontSize: '14px',
            color: '#c4b5fd',
            fontWeight: '600',
            marginBottom: '4px',
          }}>
            {center.name}
          </div>
          <div style={{
            fontSize: '12px',
            color: 'rgba(200, 180, 255, 0.7)',
          }}>
            {center.keywords}
          </div>
        </div>

        {/* Corners */}
        {corners.map((arc, i) => (
          <div key={`corner-${i}`} style={{
            background: 'rgba(30, 20, 60, 0.6)',
            border: '1px solid rgba(147, 130, 220, 0.3)',
            borderRadius: '10px',
            padding: '12px',
            textAlign: 'center',
          }}>
            <div style={{
              fontSize: '10px',
              color: 'rgba(200, 180, 255, 0.5)',
              textTransform: 'uppercase',
              marginBottom: '4px',
            }}>
              {cornerLabels[i]}
            </div>
            <div style={{
              fontSize: '22px',
              fontWeight: 'bold',
              color: '#c4b5fd',
              marginBottom: '2px',
            }}>
              {arc.number}
            </div>
            <div style={{
              fontSize: '12px',
              color: '#a78bfa',
              fontWeight: '500',
            }}>
              {arc.name}
            </div>
          </div>
        ))}
      </div>

      {/* Edges */}
      <div style={{
        background: 'rgba(20, 10, 50, 0.6)',
        borderRadius: '12px',
        padding: '14px',
        marginBottom: '20px',
      }}>
        <h3 style={{
          color: '#c4b5fd',
          fontSize: '14px',
          marginBottom: '10px',
          textAlign: 'center',
        }}>
          Промежуточные точки
        </h3>
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '16px',
          flexWrap: 'wrap',
        }}>
          {edges.map((arc, i) => (
            <div key={`edge-${i}`} style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}>
              <span style={{
                width: '28px',
                height: '28px',
                borderRadius: '50%',
                background: 'rgba(60, 40, 120, 0.8)',
                border: '1.5px solid rgba(180, 160, 240, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '12px',
                color: '#d0c0ee',
                fontWeight: 'bold',
              }}>
                {arc.number}
              </span>
              <span style={{
                fontSize: '11px',
                color: 'rgba(200, 180, 255, 0.7)',
              }}>
                {arc.name}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* AI Interpretation */}
      {data.aiInterpretation && (
        <div style={{
          background: 'rgba(30, 20, 60, 0.6)',
          border: '1px solid rgba(147, 130, 220, 0.3)',
          borderRadius: '12px',
          padding: '16px',
        }}>
          <h3 style={{
            color: '#c4b5fd',
            fontSize: '14px',
            marginBottom: '10px',
          }}>
            Интерпретация
          </h3>
          <div style={{
            color: 'rgba(220, 210, 255, 0.9)',
            fontSize: '13px',
            lineHeight: '1.7',
            whiteSpace: 'pre-wrap',
          }}>
            {data.aiInterpretation}
          </div>
        </div>
      )}
    </div>
  );
};

export default DestinyMatrix;
